import { Icon } from '@iconify/react';
import Pagination from '../../../../../components/common/Pagination';

// The card every VMS list screen lives in: title block, optional filter bar,
// body, pagination. Keeping it here means all seven modules share one set of
// paddings, borders and empty states instead of drifting apart.

export const VmsLoader = ({ label = 'Loading...' }) => (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-12">
        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-sky-500"></div>
        <span className="tw-text-xs tw-text-gray-400">{label}</span>
    </div>
);

// Two different empties: a filtered dead end the user created and can undo, and
// a genuinely empty module that should invite the first record.
export const VmsEmptyState = ({
    icon = 'mdi:inbox-outline',
    filtered,
    filteredTitle = 'Nothing matches these filters',
    filteredHint = 'Try a different combination.',
    emptyTitle = 'Nothing here yet',
    emptyHint,
    onClear,
    action,
}) => (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-14 tw-px-4 tw-text-center">
        <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-text-gray-400 tw-flex tw-items-center tw-justify-center">
            <Icon icon={icon} className="tw-text-2xl" />
        </div>
        <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-text-gray-600">
            {filtered ? filteredTitle : emptyTitle}
        </p>
        <p className="tw-mb-0 tw-text-[11px] tw-text-gray-400 tw-max-w-sm">
            {filtered ? filteredHint : emptyHint}
        </p>
        {filtered ? (
            <button
                onClick={onClear}
                className="tw-mt-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
            >
                Clear filters
            </button>
        ) : action}
    </div>
);

const VmsListShell = ({
    title,
    subtitle,
    headerActions,
    filters,
    children,
    list,
    itemLabel = 'records',
    showPagination = true,
}) => (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <div>
                <h1 className="tw-mb-0 tw-text-base tw-font-bold tw-text-gray-800">{title}</h1>
                {subtitle && <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{subtitle}</p>}
            </div>
            {headerActions && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">{headerActions}</div>
            )}
        </div>

        {filters && (
            <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
                {filters}
            </div>
        )}

        {children}

        {showPagination && !list.loading && list.rows.length > 0 && (
            <div className="tw-p-4 tw-border-t tw-border-gray-100">
                <Pagination
                    currentPage={list.page}
                    totalPages={list.totalPages}
                    totalItems={list.totalItems}
                    itemsPerPage={list.itemsPerPage}
                    onPageChange={list.setPage}
                    onItemsPerPageChange={list.setItemsPerPage}
                    itemsPerPageOptions={[10, 20, 50, 100]}
                    itemLabel={itemLabel}
                />
            </div>
        )}
    </div>
);

export default VmsListShell;
